import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { TDatasource, UUID } from '@/types';
import { csrfToken } from '@/util/helpers/ajax-helpers';
import googleTagManager from '@/integrations/googleTagManager';

export type TDatasourcesInfoResponse = {
  isHerokuAddon: boolean;
  isSuperAdmin: boolean;
  email: string;
  selfHosted: boolean;
  datasources: TDatasource[];
};

export const GET_DATASOURCES = 'getDatasources';
export const CREATE_SAMPLE_DATABASE = 'createSampleDatabase';

const DATASOURCE_LIST_TAG = 'datasource-list';

export const datasourcesApi = createApi({
  reducerPath: 'datasourcesApi',
  tagTypes: [DATASOURCE_LIST_TAG],
  baseQuery: fetchBaseQuery({
    baseUrl: '/datasources/',
    credentials: 'same-origin',
    prepareHeaders: (headers) => {
      headers.set('Content-Type', 'application/json;charset=UTF-8');
      headers.set('x-requested-with', 'XMLHttpRequest');

      const token = csrfToken();
      if (token) headers.set('x-csrf-token', token);

      return headers;
    },
  }),
  endpoints: (builder) => ({
    [GET_DATASOURCES]: builder.query<TDatasourcesInfoResponse, void>({
      query: () => 'list',
      providesTags: [DATASOURCE_LIST_TAG],
      keepUnusedDataFor: 0,
    }),
    [CREATE_SAMPLE_DATABASE]: builder.mutation<{ datasourceId: UUID }, void>({
      query: () => ({
        method: 'POST',
        url: `create-sample-datasource`,
      }),
      async onQueryStarted(id, { queryFulfilled }) {
        try {
          await queryFulfilled;
          googleTagManager.databaseSampleConnected();
          // eslint-disable-next-line no-empty
        } catch {}
      },
      invalidatesTags: [DATASOURCE_LIST_TAG],
    }),
  }),
});

export const { useGetDatasourcesQuery, useCreateSampleDatabaseMutation } =
  datasourcesApi;
